/** App shell layout — sidebar navigation + content area (SPA_MUI_ONLY). */

import { useEffect, useState, type ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ExtensionIcon from "@mui/icons-material/Extension";
import WorkIcon from "@mui/icons-material/Work";
import CompareIcon from "@mui/icons-material/Compare";
import ChatIcon from "@mui/icons-material/Chat";
import ScheduleIcon from "@mui/icons-material/Schedule";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SettingsIcon from "@mui/icons-material/Settings";
import { useAuth } from "@/hooks/useAuth";
import { useFeatureGate } from "@/hooks/useFeatureGate";

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 64;

const NAV_ITEMS = [
  { label: "Dashboard", path: "/", icon: <DashboardIcon /> },
  { label: "Flows", path: "/plugins", icon: <ExtensionIcon /> },
  { label: "Jobs", path: "/jobs", icon: <WorkIcon /> },
  { label: "Compare", path: "/compare", icon: <CompareIcon /> },
  { label: "Prompts", path: "/prompts", icon: <ChatIcon /> },
  { label: "Schedules", path: "/schedules", icon: <ScheduleIcon /> },
];

const ADMIN_ITEMS = [
  { label: "Flows", path: "/admin/plugins", icon: <AdminPanelSettingsIcon /> },
  { label: "Credentials", path: "/admin/credentials", icon: <VpnKeyIcon /> },
  { label: "Settings", path: "/admin/settings", icon: <SettingsIcon /> },
];

export default function AppShell({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { hasAccess: isAdmin, tier } = useFeatureGate("admin");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [gravatarUrl, setGravatarUrl] = useState("");

  const givenName = (user?.profile?.given_name as string) ?? "";
  const familyName = (user?.profile?.family_name as string) ?? "";
  const displayName = [givenName, familyName].filter(Boolean).join(" ") || user?.profile?.email || "Staff";
  const tierLabel: Record<string, string> = { user: "User", power_user: "Power User", admin: "Admin" };

  const currentDrawerWidth = desktopCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  useEffect(() => {
    const email = user?.profile?.email;
    if (!email) return;
    crypto.subtle.digest("SHA-256", new TextEncoder().encode(email.trim().toLowerCase())).then((buf) => {
      const hex = Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
      setGravatarUrl(`https://www.gravatar.com/avatar/${hex}?d=mp&s=80`);
    }).catch(() => {
      setGravatarUrl("https://www.gravatar.com/avatar/?d=mp&s=80");
    });
  }, [user?.profile?.email]);

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar>
        {!desktopCollapsed && (
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
            Reporting Platform
          </Typography>
        )}
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1 }}>
        {NAV_ITEMS.map((item) => (
          <ListItemButton
            key={item.path}
            selected={location.pathname === item.path}
            onClick={() => {
              navigate(item.path);
              if (isMobile) setMobileOpen(false);
            }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            {!desktopCollapsed && <ListItemText primary={item.label} />}
          </ListItemButton>
        ))}
        {isAdmin && (
          <>
            <Divider sx={{ my: 1 }} />
            {!desktopCollapsed && (
              <Typography variant="caption" sx={{ px: 2, color: "text.secondary" }}>
                Admin
              </Typography>
            )}
            {ADMIN_ITEMS.map((item) => (
              <ListItemButton
                key={item.path}
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                {!desktopCollapsed && <ListItemText primary={item.label} />}
              </ListItemButton>
            ))}
          </>
        )}
      </List>
      {!isMobile && (
        <Box sx={{ borderTop: 1, borderColor: "divider", display: "flex", justifyContent: "center", py: 1 }}>
          <IconButton onClick={() => setDesktopCollapsed((c) => !c)} aria-label={desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
            {desktopCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: "secondary.main",
          color: "#fff",
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Box component="img" src="/logo_selerity_reverse.png" alt="Selerity" sx={{ height: 32, mr: 2 }} />
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            Reporting Platform
          </Typography>
          <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)} data-testid="user-menu-button">
            <Avatar src={gravatarUrl} alt={displayName} sx={{ width: 32, height: 32 }} />
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled>{displayName}</MenuItem>
            <MenuItem disabled>{tierLabel[tier] ?? "User"}</MenuItem>
            <MenuItem disabled sx={{ fontSize: "0.75rem", minHeight: "auto", py: 0.5 }}>{__APP_VERSION__}</MenuItem>
            <Divider />
            <MenuItem onClick={() => { setAnchorEl(null); logout(); }}>Sign out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: currentDrawerWidth }, flexShrink: { md: 0 }, transition: "width 0.2s" }}>
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            sx={{ "& .MuiDrawer-paper": { width: DRAWER_WIDTH } }}
          >
            {drawer}
          </Drawer>
        ) : (
          <Drawer
            variant="permanent"
            sx={{ "& .MuiDrawer-paper": { width: currentDrawerWidth, boxSizing: "border-box", transition: "width 0.2s", overflowX: "hidden" } }}
            open
          >
            {drawer}
          </Drawer>
        )}
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: { md: `calc(100% - ${currentDrawerWidth}px)` }, transition: "width 0.2s" }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
